export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

export type RequestOptions = {
  token?: string | null;
  signal?: AbortSignal;
};

export const DEFAULT_API_BASE_URL = "https://assistente-educacional.onrender.com";
export const API_V1_PREFIX = "/api/v1";

export function resolveApiBaseUrl(): string {
  // @ts-ignore - injected by build tool
  const env = typeof process !== "undefined" ? process.env : {};
  
  const fromEnv =
    env.BUN_PUBLIC_API_URL ||
    env.VITE_API_URL ||
    env.PUBLIC_API_URL ||
    // @ts-ignore
    import.meta.env?.BUN_PUBLIC_API_URL ||
    // @ts-ignore
    import.meta.env?.VITE_API_URL;

  if (typeof fromEnv === "string" && fromEnv.trim() && fromEnv !== "undefined" && fromEnv.length > 0) {
    return fromEnv.trim().replace(/\/+$/, "");
  }
  
  return DEFAULT_API_BASE_URL;
}

export function toUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;

  const base = resolveApiBaseUrl();

  if (!base) {
    return path.startsWith("/") ? path : `/${path}`;
  }

  if (path.startsWith("/")) return `${base}${path}`;
  return `${base}/${path}`;
}

async function parseJsonOrText(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

export async function request<T>(
  path: string,
  init: RequestInit = {},
  options: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  headers.set("ngrok-skip-browser-warning", "true");

  if (options.token) {
    headers.set("authorization", `Bearer ${options.token}`);
  }

  let res: Response;
  try {
    const url = toUrl(path);
    console.log(`🌐 Fetching: ${init.method ?? "GET"} ${url}`);
    res = await fetch(url, {
      ...init,
      headers,
      signal: options.signal ?? init.signal,
    });
  } catch (error) {
    console.error("❌ Network error:", error);
    const message =
      error instanceof Error
        ? error.message
        : "Falha de conexão com o servidor";
    throw new ApiError(0, message, error);
  }

  if (res.ok) {
    if (res.status === 204) return null as T;
    const data = (await parseJsonOrText(res)) as T;
    return data;
  }

  const errorBody = await parseJsonOrText(res);

  // Handle JWT expired specifically for Supabase/PostgREST
  if (res.status === 401 || (errorBody && typeof errorBody === "object" && (errorBody as any).code === "PGRST303")) {
    // If we are in the browser, notify AuthContext via custom event
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("auth-error", { detail: { status: res.status, body: errorBody } }));
    }
  }

  const detail = (() => {
    if (errorBody && typeof errorBody === "object" && "detail" in errorBody) {
      const d = (errorBody as { detail: unknown }).detail;
      if (typeof d === "string") return d;
      try {
        return JSON.stringify(d);
      } catch {
        return String(d);
      }
    }
    return res.statusText || "Request failed";
  })();
  throw new ApiError(res.status, detail, errorBody);
}
